import React, { useState } from "react";
import { Row, Col, Card, Statistic, Button, Modal } from "antd";
import {
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  FileTextOutlined,
  CustomerServiceOutlined,
  AreaChartOutlined,
} from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import { dashboardData } from "../utils/mockData";
import type { EChartsOption } from "echarts";

interface TrendData {
  date?: string;
  month?: string;
  value?: number;
  revenue?: number;
  serviceCount?: number;
  reportCount?: number;
}

const Dashboard: React.FC = () => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalChartOption, setModalChartOption] = useState<EChartsOption>({});

  // 显示趋势图的模态框
  const showTrendModal = (title: string, data: TrendData[], type: string) => {
    let option: EChartsOption = {};

    if (type === "userActivity" || type === "customer") {
      option = {
        tooltip: { trigger: "axis" },
        xAxis: {
          type: "category",
          data: data
            .map((item) => (item.date || item.month) as string)
            .filter(Boolean),
        },
        yAxis: { type: "value" },
        series: [
          {
            data: data.map((item) => item.value).filter((v) => v !== undefined),
            type: "line",
            smooth: true,
            itemStyle: { color: "#1890ff" },
          },
        ],
      };
    } else if (type === "metrics") {
      option = {
        tooltip: { trigger: "axis" },
        legend: { data: ["收款", "服务次数", "报告发送"] },
        xAxis: {
          type: "category",
          data: data
            .map((item) => (item.date || item.month) as string)
            .filter(Boolean),
        },
        yAxis: [
          { type: "value", name: "金额(元)" },
          { type: "value", name: "次数" },
        ],
        series: [
          {
            name: "收款",
            type: "line",
            yAxisIndex: 0,
            data: data
              .map((item) => item.revenue)
              .filter((v) => v !== undefined),
          },
          {
            name: "服务次数",
            type: "line",
            yAxisIndex: 1,
            data: data
              .map((item) => item.serviceCount)
              .filter((v) => v !== undefined),
          },
          {
            name: "报告发送",
            type: "line",
            yAxisIndex: 1,
            data: data
              .map((item) => item.reportCount)
              .filter((v) => v !== undefined),
          },
        ],
      };
    }

    setModalTitle(title);
    setModalChartOption(option);
    setModalVisible(true);
  };

  // 地区分布饼图配置
  const regionChartOption: EChartsOption = {
    title: { text: "客户地区分布", left: "center" },
    tooltip: { trigger: "item" },
    series: [
      {
        type: "pie",
        radius: "50%",
        data: dashboardData.customerStats.regionDistribution.map((item) => ({
          value: item.count,
          name: item.region,
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      },
    ],
  };

  // 年龄分布柱状图配置
  const ageChartOption: EChartsOption = {
    title: { text: "客户年龄分布", left: "center" },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: dashboardData.customerStats.ageDistribution.map(
        (item) => item.ageRange
      ),
    },
    yAxis: { type: "value" },
    series: [
      {
        data: dashboardData.customerStats.ageDistribution.map(
          (item) => item.count
        ),
        type: "bar",
        itemStyle: { color: "#1890ff" },
      },
    ],
  };

  return (
    <div>
      {/* 用户活跃度 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card>
            <Statistic
              title="日活用户量"
              value={dashboardData.userActivity.dailyActive}
              prefix={<UserOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "日活用户趋势",
                      dashboardData.userActivity.dailyTrend,
                      "userActivity"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="月活用户量"
              value={dashboardData.userActivity.monthlyActive}
              prefix={<UserOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "月活用户趋势",
                      dashboardData.userActivity.monthlyTrend,
                      "userActivity"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* 客户统计 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Row gutter={16}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="当前客户数量"
                  value={dashboardData.customerStats.total}
                  prefix={<TeamOutlined />}
                  suffix={
                    <Button
                      type="link"
                      icon={<AreaChartOutlined />}
                      onClick={() =>
                        showTrendModal(
                          "当前客户数量趋势",
                          dashboardData.customerStats.totalTrend,
                          "customer"
                        )
                      }
                    >
                      查看趋势
                    </Button>
                  }
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="本月新增客户量"
                  value={dashboardData.customerStats.monthlyNew}
                  prefix={<TeamOutlined />}
                  suffix={
                    <Button
                      type="link"
                      icon={<AreaChartOutlined />}
                      onClick={() =>
                        showTrendModal(
                          "月新增客户量趋势",
                          dashboardData.customerStats.monthlyNewTrend,
                          "customer"
                        )
                      }
                    >
                      查看趋势
                    </Button>
                  }
                />
              </Card>
            </Col>
            <Col span={12} style={{ marginTop: 16 }}>
              <Card>
                <Statistic
                  title="本周新增客户量"
                  value={dashboardData.customerStats.weeklyNew}
                  prefix={<TeamOutlined />}
                  suffix={
                    <Button
                      type="link"
                      icon={<AreaChartOutlined />}
                      onClick={() =>
                        showTrendModal(
                          "周新增客户量趋势",
                          dashboardData.customerStats.weeklyNewTrend,
                          "customer"
                        )
                      }
                    >
                      查看趋势
                    </Button>
                  }
                />
              </Card>
            </Col>
            <Col span={12} style={{ marginTop: 16 }}>
              <Card>
                <Statistic
                  title="本日新增客户量"
                  value={dashboardData.customerStats.dailyNew}
                  prefix={<TeamOutlined />}
                  suffix={
                    <Button
                      type="link"
                      icon={<AreaChartOutlined />}
                      onClick={() =>
                        showTrendModal(
                          "日新增客户量趋势",
                          dashboardData.customerStats.dailyNewTrend,
                          "customer"
                        )
                      }
                    >
                      查看趋势
                    </Button>
                  }
                />
              </Card>
            </Col>
          </Row>
        </Col>
        <Col span={6}>
          <Card title="客户地区分布">
            <ReactECharts option={regionChartOption} style={{ height: 300 }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card title="客户年龄分布">
            <ReactECharts option={ageChartOption} style={{ height: 300 }} />
          </Card>
        </Col>
      </Row>

      {/* 日运营指标 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="日收款"
              value={dashboardData.dailyMetrics.revenue}
              prefix={<DollarOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "日收款趋势",
                      dashboardData.dailyMetrics.trend,
                      "metrics"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="日服务次数"
              value={dashboardData.dailyMetrics.serviceCount}
              prefix={<CustomerServiceOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "日服务次数趋势",
                      dashboardData.dailyMetrics.trend,
                      "metrics"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="日报告发送次数"
              value={dashboardData.dailyMetrics.reportCount}
              prefix={<FileTextOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "日报告发送趋势",
                      dashboardData.dailyMetrics.trend,
                      "metrics"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* 月运营指标 */}
      <Row gutter={16}>
        <Col span={8}>
          <Card>
            <Statistic
              title="月收款"
              value={dashboardData.monthlyMetrics.revenue}
              prefix={<DollarOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "月收款趋势",
                      dashboardData.monthlyMetrics.trend,
                      "metrics"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="月服务次数"
              value={dashboardData.monthlyMetrics.serviceCount}
              prefix={<CustomerServiceOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "月服务次数趋势",
                      dashboardData.monthlyMetrics.trend,
                      "metrics"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="月报告发送次数"
              value={dashboardData.monthlyMetrics.reportCount}
              prefix={<FileTextOutlined />}
              suffix={
                <Button
                  type="link"
                  icon={<AreaChartOutlined />}
                  onClick={() =>
                    showTrendModal(
                      "月报告发送趋势",
                      dashboardData.monthlyMetrics.trend,
                      "metrics"
                    )
                  }
                >
                  查看趋势
                </Button>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* 趋势图模态框 */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <ReactECharts option={modalChartOption} style={{ height: 400 }} />
      </Modal>
    </div>
  );
};

export default Dashboard;
